import { useDisclosure } from '@nextui-org/react';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useReactToPrint } from 'react-to-print';
import {
  Theme,
  ThemeContext,
} from '../../../../../contexts/ThemeContext/ThemeContext';
import { IBadRequestResponse } from '../../../../../interfaces/IBadRequestResponse';
import { INotfisResponse } from '../../../../../interfaces/response/Notfis/INotfisResponse';
import {
  postCheckFastOrder,
  postCheckOrder,
} from '../../../../../redux/features/orders/ordersThunk';
import { useAsyncDispatch } from '../../../../../redux/store';
import { ECargoType } from '../../../hooks/useCheckOrders';

interface IUsePrintOrderTagModal {
  isOpen: boolean;
  onOpenChange: () => void;
  theme: Theme;
  handleOpenModal: (nfKey: string, auto: boolean) => void;
  handleOpenModalToShowTag: (printedTag: any) => void;
  handleCloseModal: () => void;
  handlePrint: () => void;
  loading: boolean;
  tagData: any | null;
  tagRef: MutableRefObject<null>;
}

interface IUsePrintOrderTagModalProps {
  resetNfeKeyField: () => void;
  tipoCarga: ECargoType;
}

export const usePrintOrderTagModal = ({
  resetNfeKeyField,
  tipoCarga,
}: IUsePrintOrderTagModalProps): IUsePrintOrderTagModal => {
  const dispatch = useAsyncDispatch();
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const { theme } = useContext(ThemeContext);
  const cancelTokenRef = useRef<CancelTokenSource>();
  const cancelToken = axios.CancelToken;
  const [loading, setLoading] = useState<boolean>(false);
  const [automaticMode, setAutomaticMode] = useState<boolean | null>(null);
  const [tagData, setTagData] = useState<INotfisResponse | null>(null);

  const tagRef = useRef(null);

  const printableComponentRef = useCallback(() => {
    return tagRef.current;
  }, [tagRef.current]);

  const handleCloseModal = (): void => {
    onClose();
    setTagData(null);
    setAutomaticMode(null);
    resetNfeKeyField();
  };

  const handleOpenModalToShowTag = (printedTag: any): void => {
    onOpen();
    setAutomaticMode(null);
    setTagData(printedTag);
  };

  const handlePrint = useReactToPrint({
    content: printableComponentRef,
    documentTitle: 'Etiqueta',
    onAfterPrint: handleCloseModal,
    removeAfterPrint: true,
  });

  const handleCheckOrder = async (nfKey: string): Promise<void> => {
    setLoading(true);
    const source: CancelTokenSource = cancelToken.source();
    cancelTokenRef.current = source;

    await dispatch(
      postCheckOrder({
        body: { nfKey, entrega: tipoCarga === ECargoType.Entrega },
        cancelToken: source,
      })
    )
      .unwrap()
      .then((response: INotfisResponse) => {
        setTagData(response);
        toast.success('Pedido conferido com sucesso');
      })
      .catch((error: any) => {
        const axiosError = error as AxiosError<IBadRequestResponse>;
        if (axiosError.code === 'ERR_CANCELED') return;
        handleCloseModal();

        toast.error('Erro ao conferir pedido, por favor tente mais tarde');
      })
      .finally(() => setLoading(false));
  };

  const handleCheckFastOrder = async (nfKey: string): Promise<void> => {
    setLoading(true);
    const source: CancelTokenSource = cancelToken.source();
    cancelTokenRef.current = source;

    await dispatch(
      postCheckFastOrder({
        body: { nfKey, entrega: tipoCarga === ECargoType.Entrega },
        cancelToken: source,
      })
    )
      .unwrap()
      .then((response: any) => {
        setTagData(response);
        toast.success('Pedido conferido com sucesso');
      })
      .catch((error: any) => {
        const axiosError = error as AxiosError<IBadRequestResponse>;
        if (axiosError.code === 'ERR_CANCELED') return;
        handleCloseModal();

        toast.error('Erro ao conferir pedido, por favor tente mais tarde');
      })
      .finally(() => setLoading(false));
  };

  const handleOpenModal = (nfKey: string, auto: boolean): void => {
    onOpen();
    setAutomaticMode(auto);
    if (nfKey.length === 29) {
      handleCheckFastOrder(nfKey);
    } else {
      handleCheckOrder(nfKey);
    }
  };

  useEffect(() => {
    if (!automaticMode || tagRef.current === null || tagData === null) return;
    handlePrint();
  }, [automaticMode, tagRef.current, tagData]);

  return {
    isOpen,
    onOpenChange,
    theme,
    handleOpenModal,
    handleOpenModalToShowTag,
    handleCloseModal,
    handlePrint,
    loading,
    tagData,
    tagRef,
  };
};
